/**
 * Protected route wrapper component.
 * Redirects unauthenticated users to the login page.
 * Redirects authenticated but unapproved users to a pending-approval page.
 *
 * Features:
 * - Checks if user is authenticated
 * - Checks if user is approved
 * - Redirects to /login if not authenticated
 * - Redirects to /pending-approval if authenticated but not approved
 * - Shows loading spinner while checking auth
 * - Renders child routes if authenticated and approved
 *
 * Called by:
 * - App.tsx to wrap protected route definitions
 */
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import { useAuth } from '../hooks/useAuth';

/**
 * Protected route component.
 * Redirects to login if user is not authenticated.
 * Redirects to pending-approval if user is not approved.
 */
function ProtectedRoute() {
  const { user, isAuthenticated, isApproved, isBlocked, isLoading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: '#121212',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  // Preserve the attempted URL for redirect after login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to blocked page if account is blocked
  if (isBlocked) {
    return <Navigate to="/blocked" replace />;
  }

  // Redirect to pending-approval if authenticated but not approved
  if (!isApproved) {
    return <Navigate to="/pending-approval" replace />;
  }

  // Commenters can only comment on public essays — no reviewer portal access
  if (user?.role === 'commenter') {
    return <Navigate to="/home" replace />;
  }

  // Render nested routes
  return <Outlet />;
}

export default ProtectedRoute;
